import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBack from '@mui/icons-material/ArrowBack';
import PageHeader from '../../../Components/PageHeader';
import CountrySelector from '../../../Components/CountrySelector';
import StateSelector from '../../../Components/StateSelector';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import { createVendor } from '../../../endpoints/chapterSupplies';
import { getPostalInfo } from '../../../endpoints/lookupsTs';
import type { CreateVendorRequest } from '../../../types/chapterSupplies';
import {
  emailValidator,
  phoneNumberValidator,
} from '../../../utils/validators';

const initialForm: CreateVendorRequest = {
  name: '',
  contact_name: null,
  contact_email: null,
  contact_phone: null,
  address_line1: null,
  address_line2: null,
  city: null,
  state_id: null,
  postal_code: null,
  country_id: null,
  notes: null,
};

export default function VendorCreate() {
  const navigate = useNavigate();
  const [form, setForm] = useState<CreateVendorRequest>(initialForm);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  const postalCode = form.postal_code?.trim() ?? '';
  const countryId = form.country_id ?? '';

  const { data: postalInfo } = useApiGet(
    ['postal-info', postalCode, countryId],
    () => getPostalInfo(postalCode, countryId),
    { enabled: postalCode.length > 0 && countryId.length > 0, retry: 1 }
  );

  useEffect(() => {
    if (postalInfo) {
      setForm((prev) => ({
        ...prev,
        city: postalInfo.city ?? prev.city,
        state_id: postalInfo.state_id ?? prev.state_id,
      }));
    }
  }, [postalInfo]);

  const { mutate: submitVendor, isPending } = useApiSend(
    (data: CreateVendorRequest) => createVendor(data),
    (response) => {
      navigate(`/chapter-supplies-management/vendors/${response.id}`);
    },
    (err) => {
      setError(err?.message ?? 'Failed to create vendor');
    }
  );

  const handleChange = (
    field: keyof CreateVendorRequest,
    value: string | null
  ) => {
    setForm((prev) => ({ ...prev, [field]: value || null }));
    setError(null);
    if (field === 'contact_email') setEmailError(null);
    if (field === 'contact_phone') setPhoneError(null);
  };

  const handleCountryChange = (value: { id: string } | null) => {
    setForm((prev) => ({
      ...prev,
      country_id: value?.id ?? null,
      state_id: null,
    }));
    setError(null);
  };

  const handleStateChange = (value: { id: string } | null) => {
    setForm((prev) => ({ ...prev, state_id: value?.id ?? null }));
    setError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);
    setPhoneError(null);
    if (!form.name?.trim()) {
      setError('Name is required');
      return;
    }
    const emailVal = form.contact_email?.trim();
    if (emailVal) {
      const emailErr = emailValidator(emailVal);
      if (emailErr) {
        setEmailError(emailErr);
        return;
      }
    }
    const phoneVal = form.contact_phone?.trim();
    if (phoneVal) {
      const phoneErr = phoneNumberValidator(phoneVal);
      if (phoneErr) {
        setPhoneError(phoneErr);
        return;
      }
    }
    const payload: CreateVendorRequest = {
      name: form.name.trim(),
      contact_name: form.contact_name?.trim() || null,
      contact_email: form.contact_email?.trim() || null,
      contact_phone: form.contact_phone?.trim() || null,
      address_line1: form.address_line1?.trim() || null,
      address_line2: form.address_line2?.trim() || null,
      city: form.city?.trim() || null,
      state_id: form.state_id?.trim() || null,
      postal_code: form.postal_code?.trim() || null,
      country_id: form.country_id?.trim() || null,
      notes: form.notes?.trim() || null,
    };
    submitVendor(payload);
  };

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12}>
        <PageHeader
          title="Create vendor"
          leftDom={
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/chapter-supplies-management/vendors')}
            >
              Back to list
            </Button>
          }
          rightDom={undefined}
          subtitle={undefined}
        />
      </Grid>
      <Grid item xs={12} sx={{ mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                value={form.name ?? ''}
                onChange={(e) => handleChange('name', e.target.value)}
                error={!!error && !form.name?.trim()}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact name"
                value={form.contact_name ?? ''}
                onChange={(e) => handleChange('contact_name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact email"
                type="email"
                value={form.contact_email ?? ''}
                onChange={(e) => handleChange('contact_email', e.target.value)}
                error={!!emailError}
                helperText={emailError ?? ''}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact phone"
                value={form.contact_phone ?? ''}
                onChange={(e) => handleChange('contact_phone', e.target.value)}
                error={!!phoneError}
                helperText={phoneError ?? ''}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address line 1"
                value={form.address_line1 ?? ''}
                onChange={(e) => handleChange('address_line1', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address line 2"
                value={form.address_line2 ?? ''}
                onChange={(e) => handleChange('address_line2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Postal code"
                value={form.postal_code ?? ''}
                onChange={(e) => handleChange('postal_code', e.target.value)}
                placeholder="Then select country for lookup"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CountrySelector
                value={form.country_id ?? ''}
                handleChange={handleCountryChange}
                required={false}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StateSelector
                value={form.state_id ?? ''}
                countryId={form.country_id ?? ''}
                handleChange={handleStateChange}
                required={false}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="City"
                value={form.city ?? ''}
                onChange={(e) => handleChange('city', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={form.notes ?? ''}
                onChange={(e) => handleChange('notes', e.target.value)}
              />
            </Grid>
            {error && (
              <Grid item xs={12}>
                <Typography color="error" variant="body2">
                  {error}
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  isPending ||
                  !form.name?.trim() ||
                  !!emailError ||
                  !!phoneError
                }
              >
                {isPending ? 'Creating…' : 'Create vendor'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
}
